/**
 * RichEmailBody — a Tiptap-backed WYSIWYG editor for the email REPLY body
 * (Jul 2026).
 *
 * Replaces the plain ``<textarea>`` in the Conversazioni reply composer so
 * hyperlinks (and bold / italic / lists) render styled + clickable AS THE
 * USER TYPES — the sent ``body_html`` is now the editor's own serialised
 * markup, so we no longer post-process the reply with ``linkifyHtml`` (Tiptap
 * already emits real ``<a>`` anchors via autolink / link-on-paste).
 *
 * Mirrors {@link RichTextEditor} (the chat composer) 1:1 on the controlled-value
 * pattern, the ``isDestroyed`` sync guard, and the ``_createTest*Editor`` unit
 * hook — but this surface is EMAIL, not chat:
 *   - multi-paragraph (KEEP paragraph, hardBreak, history) + bold / italic /
 *     strike + bullet / ordered lists;
 *   - heading / codeBlock / blockquote / horizontalRule are disabled to keep
 *     the body email-clean;
 *   - ``@tiptap/extension-link`` is configured for openOnClick:false + autolink
 *     + linkOnPaste, with the pencil link treatment
 *     (``text-ando-orange underline decoration-ando-orange/40``) and a hardened
 *     ``rel="noopener noreferrer nofollow" target="_blank"``.
 *   - NO Mention (email has no @-chip surface).
 *
 * A small inline toolbar wraps the CURRENT selection as a link (label stays the
 * visible text — SOTA) or removes it. Typed / pasted URLs auto-link with no
 * manual step.
 *
 * ⌘K / Ctrl+K (while focused) opens the shared {@link AIEditBubble} anchored
 * under the editor: selection → refine that span; collapsed caret → rewrite the
 * whole body. The rewrite is resolved through the ``aiEdit`` transport prop
 * (``@ando/ui`` stays free of ``@ando/core`` — the host wires it to
 * ``aiEditEmailText``), reviewed as an {@link InlineDiffPreview} redline, then
 * applied over the range (or the whole doc when collapsed).
 */

import { msg } from "@lingui/core/macro";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
// v3.27 ``@tiptap/extension-text-style`` bundles TextStyle + Color + FontFamily
// (Color / FontFamily are Extensions that write onto the TextStyle mark), so
// this ONE package is the single source — no separate color/font-family imports.
import { Color, FontFamily, TextStyle } from "@tiptap/extension-text-style";
import {
  Editor,
  EditorContent,
  type JSONContent,
  type Editor as TiptapEditor,
  useEditor,
  useEditorState,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Baseline,
  Bold,
  ChevronDown,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  RemoveFormatting,
  Strikethrough,
  Type,
  Underline as UnderlineIcon,
  Unlink,
} from "lucide-react";
import * as React from "react";
import { type AnchorRect, useAnchoredPanel } from "../../hooks/use-anchored-panel";
import { cn } from "../../lib/cn";
import { useI18n } from "../../lib/i18n";
import { Popover, PopoverContent, PopoverTrigger } from "../../primitives/popover";
import { AIEditBubble } from "../actions/AIEditBubble";
import { type DiffChange, type DiffDecision, reconstructText } from "../actions/InlineDiffPreview";
import { LineBlobGlyph } from "../blob/LineBlobGlyph";
import { AiAutocomplete } from "./aiAutocomplete";
import { emailBodyToHtml } from "./emailBodyHtml";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Resolved per-change diff script — same shape as ``EmailAiEditResult``. */
export interface RichEmailAiEditResult {
  readonly original: string;
  readonly rewritten: string;
  readonly changes: ReadonlyArray<DiffChange>;
}

/**
 * ⌘K transport. ``@ando/ui`` does NOT depend on ``@ando/core`` (cycle), so the
 * host owns the network call — ``apps/client`` wires this to
 * ``aiEditEmailText({ text, instruction })``. When omitted the ⌘K bubble is
 * inert (no AI affordance).
 */
export type RichEmailAiEdit = (input: {
  readonly text: string;
  readonly instruction: string;
}) => Promise<RichEmailAiEditResult>;

export interface RichEmailBodyProps {
  /** Controlled HTML value. Reset into the editor only when it diverges. */
  readonly valueHtml: string;
  /** Emits both serialised HTML + plain text on every keystroke. */
  readonly onChange: (html: string, text: string) => void;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  /** Collapsed min height in px. Default ``110``. */
  readonly minHeight?: number;
  /** Max height in px before inner scroll. Default ``320``. */
  readonly maxHeight?: number;
  readonly className?: string;
  /** ⌘K AI-edit transport. Absent → the AI bubble is disabled. */
  readonly aiEdit?: RichEmailAiEdit;
  /**
   * Ghost-text autocomplete transport (Gmail Smart Compose). When supplied, a
   * debounced call fires as the user pauses typing at the end of the body; the
   * returned continuation shows as inline ghost text (Tab accepts). Absent →
   * no autocomplete.
   */
  readonly aiComplete?: (input: {
    readonly draft: string;
  }) => Promise<{ readonly suggestion: string }>;
  /**
   * Show the persistent rich-text format toolbar above the body (text style,
   * bold / italic / strike, lists, quote, link, clear). Gmail / Superhuman
   * expose formatting visibly rather than hiding it behind a selection.
   */
  readonly toolbar?: boolean;
  /**
   * Rendered INSIDE the editor sheet, below the body with a hairline separator
   * — used for the signature so it reads as part of the message ("firma sotto
   * la mail", the way Gmail shows the signature in the compose body).
   */
  readonly footer?: React.ReactNode;
  /**
   * Drop the editor's own border / background / focus-ring so it can sit inside
   * a shared "letter" sheet (unified compose). Toolbar / body / footer keep
   * their internal hairline dividers.
   */
  readonly bare?: boolean;
  /**
   * Active Brand Kit fonts ({family, fallback}) — PREPENDED above the built-in
   * generics in the "Aa" font dropdown. Each is applied as the email-safe CSS
   * chain ``'family', fallback`` so the real brand font shows for recipients
   * who have it and gracefully degrades for those who don't (custom fonts do
   * NOT render in every recipient's email client). Absent / empty → only the
   * built-ins show. Wire the real font FILES via the host's ``useBrandFonts``.
   */
  readonly brandFonts?: readonly { family: string; fallback: string }[];
}

export interface RichEmailBodyHandle {
  readonly getHTML: () => string;
  readonly getText: () => string;
  readonly focus: () => void;
  readonly setHTML: (html: string) => void;
  readonly insertText: (text: string) => void;
}

// ---------------------------------------------------------------------------
// Extension factory (shared by the live editor + the test helper — DRY)
// ---------------------------------------------------------------------------

const LINK_HTML_CLASS = "text-ando-orange underline decoration-ando-orange/40";

/** Aria fra caret e pannello AI (e fra pannello e bordo finestra). */
const AI_PANEL_GAP = 8;
/** Sotto questa soglia il pannello non è più usabile: meglio scrollarlo. */
const MIN_AI_PANEL_H = 220;
/** Larghezza del pannello AI (`w-[320px]` in `AIEditBubble`). */
const AI_PANEL_W = 320;

/**
 * The email extension set. Kept in ONE place so ``useEditor`` and
 * ``_createTestEmailEditor`` never drift.
 */
export function buildEmailExtensions(placeholder?: string) {
  return [
    StarterKit.configure({
      // Email is multi-paragraph — KEEP paragraph / bold / italic / strike /
      // hardBreak / history + lists; drop the block structures that would
      // let the reply produce non-email markup.
      // H2/H3 = the "Titolo / Sottotitolo" text styles the format toolbar
      // offers; blockquote stays on for real quoting. codeBlock /
      // horizontalRule remain off (non-email markup).
      heading: { levels: [2, 3] },
      codeBlock: false,
      horizontalRule: false,
      // StarterKit v3 bundles its own Link — disable it so our configured
      // Link (below) is the single source of truth (no duplicate-extension).
      link: false,
    }),
    ...(placeholder
      ? [
          Placeholder.configure({
            placeholder,
            emptyEditorClass:
              "before:content-[attr(data-placeholder)] before:text-ink-3 before:pointer-events-none before:absolute",
          }),
        ]
      : []),
    Link.configure({
      openOnClick: false,
      autolink: true,
      linkOnPaste: true,
      protocols: ["http", "https", "mailto"],
      HTMLAttributes: {
        rel: "noopener noreferrer nofollow",
        target: "_blank",
        class: LINK_HTML_CLASS,
      },
    }),
    // Inline text color + font-family ride the TextStyle mark (TextStyle MUST
    // precede Color / FontFamily). Underline is already provided by StarterKit
    // 3.27, so we do NOT add it again here (would duplicate the extension).
    TextStyle,
    Color,
    FontFamily,
  ];
}

/** Text-colour swatches offered by the toolbar (label + CSS value). */
export const EMAIL_TEXT_COLORS: ReadonlyArray<{ label: string; value: string }> = [
  { label: "Inchiostro", value: "#1F1D1A" },
  { label: "Grigio", value: "#56544F" },
  { label: "Arancio", value: "#EA580C" },
  { label: "Rosso", value: "#DC2626" },
  { label: "Verde", value: "#16A34A" },
  { label: "Blu", value: "#2563EB" },
  { label: "Viola", value: "#7C3AED" },
];

/** Font-family options offered by the toolbar (label + CSS stack). */
export const EMAIL_FONT_FAMILIES: ReadonlyArray<{ label: string; value: string }> = [
  { label: "Predefinito", value: "" },
  { label: "Sans-serif", value: "Arial, Helvetica, sans-serif" },
  { label: "Serif", value: "Georgia, 'Times New Roman', serif" },
  { label: "Monospazio", value: "'Courier New', ui-monospace, monospace" },
];

// ---------------------------------------------------------------------------
// Plain-text → email HTML (used for whole-doc AI replace + the setHTML helper)
// ---------------------------------------------------------------------------

function escapeHtml(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Inline HTML fragment: escaped text with newlines → ``<br>`` (span replace). */
function inlineHtml(text: string): string {
  return escapeHtml(text).replace(/\r?\n/g, "<br>");
}

/** Block HTML — paragraphs PLUS real ``<ul>``/``<ol>`` from ``•``/``1.`` runs
 *  (shared ``emailBodyToHtml``), so an AI whole-doc rewrite that returns
 *  bullet-text becomes real lists, not literal ``•`` characters. */
function paragraphsHtml(text: string): string {
  return emailBodyToHtml(text);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * One icon control inside the floating selection toolbar. ``onMouseDown``
 * preventDefault keeps the editor's text selection alive while the button is
 * pressed (a click would otherwise blur the editor and collapse the range).
 */
function BubbleButton({
  active,
  accent,
  label,
  onClick,
  children,
}: {
  readonly active?: boolean;
  readonly accent?: boolean;
  readonly label: string;
  readonly onClick: () => void;
  readonly children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        "grid h-7 w-7 place-items-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
        accent
          ? // Hover velato neutro: fondo arancione + glifo arancione si
            // annullavano a vicenda (poco contrasto, molto colore).
            "text-ando-orange hover:bg-ink-1/[0.05]"
          : active
            ? "bg-ando-orange-soft text-ando-orange"
            : "text-ink-2 hover:bg-black/[0.05] hover:text-ink-1",
      )}
    >
      {children}
    </button>
  );
}

export const RichEmailBody = React.forwardRef<RichEmailBodyHandle, RichEmailBodyProps>(
  (
    {
      valueHtml,
      onChange,
      placeholder,
      disabled,
      minHeight = 150,
      maxHeight = 360,
      className,
      aiEdit,
      aiComplete,
      toolbar,
      footer,
      bare,
      brandFonts,
    },
    ref,
  ) => {
    const { i18n } = useI18n();
    const resolvedPlaceholder = placeholder ?? i18n._(msg`Scrivi la tua risposta…`);
    const onChangeRef = React.useRef(onChange);
    React.useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    const editor = useEditor({
      extensions: [
        ...buildEmailExtensions(resolvedPlaceholder),
        // Ghost-text autocomplete is only wired when a transport is supplied
        // (kept out of ``buildEmailExtensions`` so the headless test editor and
        // non-autocomplete callers stay lean).
        ...(aiComplete ? [AiAutocomplete] : []),
      ],
      content: valueHtml || "",
      editable: !disabled,
      editorProps: {
        attributes: {
          role: "textbox",
          "aria-label": i18n._(msg`Corpo della risposta`),
          "aria-multiline": "true",
          class: cn(
            // ``relative`` anchors the empty-state placeholder (``before:absolute``)
            // to the editor node itself, not the outer wrapper (which now also
            // holds the format toolbar).
            "relative block w-full resize-none border-0 bg-transparent outline-none",
            "px-3 py-2 text-[12px] text-ink-1 leading-relaxed",
            "whitespace-pre-wrap break-words scrollbar-thin",
            "[&_p]:my-0 [&_a]:cursor-text",
            "[&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5",
            "[&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5",
            "[&_h2]:mt-1.5 [&_h2]:font-semibold [&_h2]:text-[15px] [&_h2]:text-ink-1",
            "[&_h3]:mt-1 [&_h3]:font-semibold [&_h3]:text-[13.5px] [&_h3]:text-ink-1",
            "[&_blockquote]:my-1 [&_blockquote]:border-hairline [&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:text-ink-2",
            className,
          ),
          style: `min-height:${minHeight}px;max-height:${maxHeight}px;overflow-y:auto;`,
        },
      },
      onUpdate: ({ editor: ed }) => {
        if (ed.isDestroyed) return;
        onChangeRef.current?.(ed.getHTML(), ed.getText({ blockSeparator: "\n" }));
      },
    });

    // Keep editability in sync with the ``disabled`` prop.
    React.useEffect(() => {
      if (!editor || editor.isDestroyed) return;
      editor.setEditable(!disabled);
    }, [editor, disabled]);

    // Controlled ``valueHtml`` → editor. Only reset when it diverges from the
    // editor's own serialisation (avoids clobbering the caret / marks on every
    // keystroke, and guards the strict-mode ``isDestroyed`` reconnect path).
    React.useEffect(() => {
      if (!editor || editor.isDestroyed) return;
      // NEVER re-sync while the user is actively typing — the editor is the
      // source of truth then. A controlled ``valueHtml`` that momentarily
      // diverges (e.g. a lossy plaintext→HTML re-derivation upstream) would
      // otherwise trigger ``setContent`` and jump the caret to the end on every
      // keystroke / Enter. External programmatic updates use the imperative
      // ``setHTML`` handle instead, so they don't rely on this path.
      if (editor.isFocused) return;
      if (editor.getHTML() === valueHtml) return;
      editor.commands.setContent(valueHtml || "", { emitUpdate: false });
    }, [editor, valueHtml]);

    // ── Reactive re-render for the format toolbar ───────────────────────────
    // Tiptap v3 ``useEditorState`` subscribes to the editor but only re-renders
    // when the SELECTED slice actually changes (deep-compared). The toolbar
    // still reads ``editor.isActive(...)`` inline during render — but by
    // selecting every flag the toolbar cares about here, a plain keystroke
    // (no mark/list/heading flip) returns an identical slice → NO re-render,
    // while a real toggle flips a value → one re-render. This replaces the old
    // ``setTick``-on-every-transaction pattern that re-rendered per keystroke.
    const toolbarState = useEditorState({
      editor,
      selector: ({ editor: e }) => {
        if (!e) return null;
        return {
          bold: e.isActive("bold"),
          italic: e.isActive("italic"),
          underline: e.isActive("underline"),
          strike: e.isActive("strike"),
          bulletList: e.isActive("bulletList"),
          orderedList: e.isActive("orderedList"),
          blockquote: e.isActive("blockquote"),
          h2: e.isActive("heading", { level: 2 }),
          h3: e.isActive("heading", { level: 3 }),
          heading: e.isActive("heading"),
          fontFamily: (e.getAttributes("textStyle").fontFamily as string) || "",
          color: (e.getAttributes("textStyle").color as string) || "",
          linkActive: e.isActive("link"),
          linkHref: (e.getAttributes("link").href as string) ?? "",
        };
      },
    });
    const linkActive = toolbarState?.linkActive ?? false;
    const linkHref = toolbarState?.linkHref ?? "";

    // Anchor for the AI-edit panel — positioned AT the selection (Notion /
    // Claude pattern) instead of floating at a fixed corner of the sheet.
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const aiPanelRef = React.useRef<HTMLDivElement>(null);

    const [linkOpen, setLinkOpen] = React.useState(false);
    const [linkUrl, setLinkUrl] = React.useState("");

    const applyLink = React.useCallback(() => {
      const href = linkUrl.trim();
      if (!editor) return;
      if (!href) {
        setLinkOpen(false);
        return;
      }
      // extendMarkRange → editing an existing link updates the whole anchor;
      // a fresh selection is wrapped with the visible text kept as the label.
      editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
      setLinkOpen(false);
      setLinkUrl("");
    }, [editor, linkUrl]);

    const removeLink = React.useCallback(() => {
      if (!editor) return;
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    }, [editor]);

    // ── ⌘K AI edit ──────────────────────────────────────────────────────────
    const [aiOpen, setAiOpen] = React.useState(false);
    const [aiBusy, setAiBusy] = React.useState(false);
    const [aiError, setAiError] = React.useState<string | null>(null);
    const [aiDiff, setAiDiff] = React.useState<RichEmailAiEditResult | null>(null);
    const [aiDecisions, setAiDecisions] = React.useState<Record<string, DiffDecision>>({});
    const [aiRange, setAiRange] = React.useState<{ from: number; to: number }>({
      from: 0,
      to: 0,
    });
    const [lastInstruction, setLastInstruction] = React.useState("");

    const closeAi = React.useCallback(() => {
      setAiOpen(false);
      setAiDiff(null);
      setAiDecisions({});
      setAiError(null);
    }, []);

    // Ancora del pannello AI = il caret (pattern Notion / Claude). Le
    // coordinate sono di viewport: sopra/sotto e altezza massima li decide
    // `useAnchoredPanel`, che rilegge questa funzione a ogni scroll/resize.
    const readAiCaret = React.useCallback((): AnchorRect | null => {
      if (!editor) return null;
      const coords = editor.view.coordsAtPos(editor.state.selection.from);
      return { top: coords.top, bottom: coords.bottom, left: coords.left };
    }, [editor]);

    const aiAnchor = useAnchoredPanel({
      open: aiOpen,
      panelRef: aiPanelRef,
      containerRef: wrapperRef,
      anchor: readAiCaret,
      gap: AI_PANEL_GAP,
      minHeight: MIN_AI_PANEL_H,
      width: AI_PANEL_W,
      // Ogni cambio di contenuto (risultato, spinner, errore) cambia l'altezza.
      contentKey: `${aiDiff ? "diff" : "presets"}:${aiBusy}:${aiError ?? ""}`,
    });

    // Sparkles in the toolbar → same flow as ⌘K, scoped to the live selection.
    const openAiOnSelection = React.useCallback(() => {
      if (!editor || !aiEdit) return;
      const { from, to } = editor.state.selection;
      setAiRange({ from, to });
      setAiDiff(null);
      setAiDecisions({});
      setAiError(null);
      setAiOpen(true);
    }, [editor, aiEdit]);

    // ⌘K / Ctrl+K while the editor is focused: with a selection → refine it;
    // collapsed → open on the WHOLE body.
    React.useEffect(() => {
      if (!editor || !aiEdit || disabled) return;
      const handler = (ev: KeyboardEvent) => {
        if (!(ev.metaKey || ev.ctrlKey) || ev.key.toLowerCase() !== "k") return;
        if (editor.isDestroyed || !editor.isFocused) return;
        ev.preventDefault();
        const { from, to } = editor.state.selection;
        setAiRange({ from, to });
        setAiDiff(null);
        setAiDecisions({});
        setAiError(null);
        setAiOpen(true);
      };
      document.addEventListener("keydown", handler);
      return () => document.removeEventListener("keydown", handler);
    }, [editor, aiEdit, disabled]);

    // Esc closes the AI bubble and returns focus to the editor.
    React.useEffect(() => {
      if (!aiOpen) return;
      const handler = (ev: KeyboardEvent) => {
        if (ev.key === "Escape") {
          closeAi();
          editor?.commands.focus();
        }
      };
      document.addEventListener("keydown", handler);
      return () => document.removeEventListener("keydown", handler);
    }, [aiOpen, closeAi, editor]);

    // Click OUTSIDE the AI panel dismisses it (standard popover behaviour) —
    // clicking a preset chip / the input stays inside the panel and is kept.
    React.useEffect(() => {
      if (!aiOpen) return;
      const onDown = (ev: MouseEvent) => {
        if (aiPanelRef.current && !aiPanelRef.current.contains(ev.target as Node)) {
          closeAi();
        }
      };
      document.addEventListener("mousedown", onDown);
      return () => document.removeEventListener("mousedown", onDown);
    }, [aiOpen, closeAi]);

    // Debounced ghost-text autocomplete (Gmail Smart Compose). Fires only when
    // the user pauses with a collapsed caret at the very END of the body and
    // there's enough context. The BE already includes any needed leading space,
    // so the suggestion is shown verbatim. Never throws — a failed suggestion
    // must never interrupt typing. The AiAutocomplete plugin auto-clears the
    // ghost on the next keystroke / caret move.
    React.useEffect(() => {
      if (!editor || !aiComplete) return;
      let timer: ReturnType<typeof setTimeout> | undefined;
      let reqId = 0;
      const onUpdate = () => {
        if (timer) clearTimeout(timer);
        if (aiOpen) return;
        const sel = editor.state.selection;
        if (!sel.empty) return;
        const after = editor.state.doc.textBetween(sel.head, editor.state.doc.content.size);
        if (after.trim().length !== 0) return; // caret not at the end of the body
        const draft = editor.getText({ blockSeparator: "\n" });
        if (draft.trim().length < 15) return;
        const myId = ++reqId;
        timer = setTimeout(() => {
          void aiComplete({ draft })
            .then(({ suggestion }) => {
              if (myId !== reqId || editor.isDestroyed || !editor.isFocused) return;
              if (suggestion) editor.commands.setAiSuggestion(suggestion);
            })
            .catch(() => {
              /* autocomplete must never break typing */
            });
        }, 550);
      };
      editor.on("update", onUpdate);
      return () => {
        editor.off("update", onUpdate);
        if (timer) clearTimeout(timer);
      };
    }, [editor, aiComplete, aiOpen]);

    const runAiEdit = React.useCallback(
      async (instruction: string) => {
        if (!editor || !aiEdit || !instruction.trim()) return;
        const { from, to } = aiRange;
        const text =
          from === to
            ? editor.getText({ blockSeparator: "\n" })
            : editor.state.doc.textBetween(from, to, "\n");
        setAiBusy(true);
        setAiError(null);
        setLastInstruction(instruction);
        try {
          const result = await aiEdit({ text, instruction });
          setAiDiff(result);
          setAiDecisions({});
        } catch (err) {
          setAiError(err instanceof Error ? err.message : i18n._(msg`Errore di rete.`));
        } finally {
          setAiBusy(false);
        }
      },
      [editor, aiEdit, aiRange, i18n],
    );

    const applyAiEdit = React.useCallback(() => {
      if (!editor || !aiDiff) return;
      const reconstructed = reconstructText(aiDiff.original, aiDiff.changes, aiDecisions);
      const { from, to } = aiRange;
      if (from === to) {
        // Whole-body rewrite — replace the entire doc with the reconstructed
        // paragraphs (autolink re-anchors any URLs in the new text).
        editor.chain().focus().setContent(paragraphsHtml(reconstructed)).run();
      } else {
        editor.chain().focus().insertContentAt({ from, to }, inlineHtml(reconstructed)).run();
      }
      closeAi();
    }, [editor, aiDiff, aiDecisions, aiRange, closeAi]);

    const onAiDecision = React.useCallback((id: string, decision: DiffDecision) => {
      setAiDecisions((prev) => {
        if (prev[id] === decision) {
          const next = { ...prev };
          delete next[id];
          return next;
        }
        return { ...prev, [id]: decision };
      });
    }, []);

    // ── Imperative handle ─────────────────────────────────────────────────────
    React.useImperativeHandle(
      ref,
      (): RichEmailBodyHandle => ({
        getHTML: () => editor?.getHTML() ?? "",
        getText: () => editor?.getText({ blockSeparator: "\n" }) ?? "",
        focus: () => editor?.commands.focus(),
        setHTML: (html: string) => {
          if (!editor || editor.isDestroyed) return;
          // Emit an update so the controlled ``valueHtml`` mirror stays in sync
          // (used by "Genera con Ando" to drop a fresh draft into the body).
          editor.commands.setContent(html || "", { emitUpdate: true });
        },
        insertText: (text: string) => {
          if (!editor || editor.isDestroyed) return;
          editor.chain().focus().insertContent(inlineHtml(text)).run();
        },
      }),
      [editor],
    );

    return (
      <div ref={wrapperRef} className="relative">
        <div
          className={cn(
            "flex flex-col transition-colors",
            bare
              ? "bg-transparent"
              : "rounded-lg border border-hairline bg-white/70 focus-within:border-ando-orange/40 focus-within:bg-white focus-within:ring-2 focus-within:ring-ando-orange/60",
          )}
        >
          {/* Persistent rich-text toolbar (Gmail / Superhuman: formatting is
              VISIBLE, not hidden behind a selection). ``onMouseDown``
              preventDefault keeps the editor selection alive while clicking. */}
          {toolbar ? (
            <div
              role="toolbar"
              aria-label={i18n._(msg`Formattazione`)}
              onMouseDown={(e) => e.preventDefault()}
              className="flex flex-wrap items-center gap-0.5 border-hairline border-b px-1.5 py-1"
            >
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    title={i18n._(msg`Stile testo`)}
                    className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 font-medium text-[11.5px] text-ink-2 transition-colors hover:bg-black/[0.05] hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
                  >
                    <Type className="h-3.5 w-3.5 text-ink-3" strokeWidth={2} aria-hidden />
                    {editor?.isActive("heading", { level: 2 })
                      ? i18n._(msg`Titolo`)
                      : editor?.isActive("heading", { level: 3 })
                        ? i18n._(msg`Sottotitolo`)
                        : i18n._(msg`Normale`)}
                    <ChevronDown className="h-3 w-3 text-ink-4" strokeWidth={2} aria-hidden />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" sideOffset={6} className="w-[180px] p-1">
                  {(
                    [
                      { label: i18n._(msg`Normale`), cls: "text-[13px]", active: Boolean(editor && !editor.isActive("heading")), run: () => editor?.chain().focus().setParagraph().run() },
                      { label: i18n._(msg`Titolo`), cls: "text-[15px] font-semibold", active: Boolean(editor?.isActive("heading", { level: 2 })), run: () => editor?.chain().focus().toggleHeading({ level: 2 }).run() },
                      { label: i18n._(msg`Sottotitolo`), cls: "text-[13.5px] font-semibold", active: Boolean(editor?.isActive("heading", { level: 3 })), run: () => editor?.chain().focus().toggleHeading({ level: 3 }).run() },
                    ] as const
                  ).map((s) => (
                    <button
                      key={s.label}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={s.run}
                      className={cn(
                        "flex w-full items-center rounded-md px-2 py-1.5 text-left text-ink-1 transition-colors hover:bg-black/[0.04]",
                        s.cls,
                        s.active && "text-ando-orange",
                      )}
                    >
                      {s.label}
                    </button>
                  ))}
                </PopoverContent>
              </Popover>

              <span className="mx-0.5 h-4 w-px bg-hairline" aria-hidden />
              <BubbleButton active={Boolean(editor?.isActive("bold"))} onClick={() => editor?.chain().focus().toggleBold().run()} label={i18n._(msg`Grassetto`)}>
                <Bold className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
              </BubbleButton>
              <BubbleButton active={Boolean(editor?.isActive("italic"))} onClick={() => editor?.chain().focus().toggleItalic().run()} label={i18n._(msg`Corsivo`)}>
                <Italic className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
              </BubbleButton>
              <BubbleButton active={Boolean(editor?.isActive("underline"))} onClick={() => editor?.chain().focus().toggleUnderline().run()} label={i18n._(msg`Sottolineato`)}>
                <UnderlineIcon className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
              </BubbleButton>
              <BubbleButton active={Boolean(editor?.isActive("strike"))} onClick={() => editor?.chain().focus().toggleStrike().run()} label={i18n._(msg`Barrato`)}>
                <Strikethrough className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
              </BubbleButton>

              <span className="mx-0.5 h-4 w-px bg-hairline" aria-hidden />
              {/* Font family */}
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    title={i18n._(msg`Carattere`)}
                    aria-label={i18n._(msg`Carattere`)}
                    className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-1 font-medium text-[11.5px] text-ink-2 transition-colors hover:bg-black/[0.05] hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
                  >
                    <span style={{ fontFamily: (editor?.getAttributes("textStyle").fontFamily as string) || undefined }}>
                      Aa
                    </span>
                    <ChevronDown className="h-3 w-3 text-ink-4" strokeWidth={2} aria-hidden />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" sideOffset={6} className="w-[180px] p-1">
                  {(() => {
                    const current =
                      (editor?.getAttributes("textStyle").fontFamily as string) || "";
                    return (
                      <>
                        {/* Brand Kit fonts — PREPENDED above the built-in generics.
                            The stored value is the email-safe chain ``'family',
                            fallback`` (custom fonts don't render in every
                            recipient's client, so the fallback rides along).
                            Active when the stored fontFamily starts with the
                            quoted family. */}
                        {brandFonts && brandFonts.length > 0 ? (
                          <>
                            <p className="px-2 pt-1 pb-0.5 font-medium text-[10px] text-ink-3 uppercase tracking-[0.05em]">
                              {i18n._(msg`Brand`)}
                            </p>
                            {brandFonts.map((f) => {
                              const value = `'${f.family}', ${f.fallback}`;
                              const active = current.startsWith(`'${f.family}'`);
                              return (
                                <button
                                  key={f.family}
                                  type="button"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() =>
                                    editor?.chain().focus().setFontFamily(value).run()
                                  }
                                  style={{ fontFamily: value }}
                                  className={cn(
                                    "flex w-full items-center rounded-md px-2 py-1.5 text-left text-[13px] text-ink-1 transition-colors hover:bg-black/[0.04]",
                                    active && "text-ando-orange",
                                  )}
                                >
                                  {f.family}
                                </button>
                              );
                            })}
                            <div className="my-1 h-px bg-hairline" aria-hidden />
                          </>
                        ) : null}
                        {EMAIL_FONT_FAMILIES.map((f) => {
                          const active = current === f.value;
                          return (
                            <button
                              key={f.label}
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                if (f.value) editor?.chain().focus().setFontFamily(f.value).run();
                                else editor?.chain().focus().unsetFontFamily().run();
                              }}
                              style={{ fontFamily: f.value || undefined }}
                              className={cn(
                                "flex w-full items-center rounded-md px-2 py-1.5 text-left text-[13px] text-ink-1 transition-colors hover:bg-black/[0.04]",
                                active && "text-ando-orange",
                              )}
                            >
                              {f.label}
                            </button>
                          );
                        })}
                      </>
                    );
                  })()}
                </PopoverContent>
              </Popover>
              {/* Text colour */}
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    title={i18n._(msg`Colore testo`)}
                    aria-label={i18n._(msg`Colore testo`)}
                    className="grid h-7 w-7 place-items-center rounded-md text-ink-2 transition-colors hover:bg-black/[0.05] hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
                  >
                    <Baseline
                      className="h-3.5 w-3.5"
                      strokeWidth={2.25}
                      aria-hidden
                      style={{ color: (editor?.getAttributes("textStyle").color as string) || undefined }}
                    />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" sideOffset={6} className="w-auto p-2">
                  <div className="flex items-center gap-1">
                    {EMAIL_TEXT_COLORS.map((c) => {
                      const active = (editor?.getAttributes("textStyle").color as string) === c.value;
                      return (
                        <button
                          key={c.value}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => editor?.chain().focus().setColor(c.value).run()}
                          title={c.label}
                          aria-label={c.label}
                          className={cn(
                            "h-5 w-5 rounded-full border transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
                            active ? "border-ink-1" : "border-black/10",
                          )}
                          style={{ backgroundColor: c.value }}
                        />
                      );
                    })}
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => editor?.chain().focus().unsetColor().run()}
                      title={i18n._(msg`Predefinito`)}
                      aria-label={i18n._(msg`Colore predefinito`)}
                      className="ml-0.5 grid h-5 w-5 place-items-center rounded-full border border-black/10 text-ink-3 transition-colors hover:text-ink-1"
                    >
                      <RemoveFormatting className="h-3 w-3" strokeWidth={2} aria-hidden />
                    </button>
                  </div>
                </PopoverContent>
              </Popover>

              <span className="mx-0.5 h-4 w-px bg-hairline" aria-hidden />
              <BubbleButton active={Boolean(editor?.isActive("bulletList"))} onClick={() => editor?.chain().focus().toggleBulletList().run()} label={i18n._(msg`Elenco puntato`)}>
                <List className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
              </BubbleButton>
              <BubbleButton active={Boolean(editor?.isActive("orderedList"))} onClick={() => editor?.chain().focus().toggleOrderedList().run()} label={i18n._(msg`Elenco numerato`)}>
                <ListOrdered className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
              </BubbleButton>
              <BubbleButton active={Boolean(editor?.isActive("blockquote"))} onClick={() => editor?.chain().focus().toggleBlockquote().run()} label={i18n._(msg`Citazione`)}>
                <Quote className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
              </BubbleButton>

              <span className="mx-0.5 h-4 w-px bg-hairline" aria-hidden />
              <Popover
                open={linkOpen}
                onOpenChange={(next) => {
                  if (next) setLinkUrl(linkHref);
                  setLinkOpen(next);
                }}
              >
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    title={linkActive ? i18n._(msg`Modifica link`) : i18n._(msg`Inserisci link`)}
                    aria-label={linkActive ? i18n._(msg`Modifica link`) : i18n._(msg`Inserisci link`)}
                    className={cn(
                      "grid h-7 w-7 place-items-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
                      linkActive
                        ? "bg-ando-orange-soft text-ando-orange"
                        : "text-ink-2 hover:bg-black/[0.05] hover:text-ink-1",
                    )}
                  >
                    <LinkIcon className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" sideOffset={8} className="w-[280px] space-y-2 p-2">
                  <label className="block space-y-1">
                    <span className="font-medium text-[10px] text-ink-3 uppercase tracking-[0.05em]">
                      {i18n._(msg`Indirizzo del link`)}
                    </span>
                    <input
                      type="url"
                      value={linkUrl}
                      onChange={(e) => setLinkUrl(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          applyLink();
                        }
                      }}
                      placeholder={i18n._(msg`https://…`)}
                      className="block w-full rounded-md border border-hairline bg-white/70 px-2 py-1.5 text-[12px] text-ink-1 placeholder:text-ink-3 focus:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
                    />
                  </label>
                  <div className="flex items-center justify-between gap-2">
                    {linkActive ? (
                      <button
                        type="button"
                        onClick={removeLink}
                        className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] text-ink-3 transition-colors hover:text-ink-1"
                      >
                        <Unlink className="h-3 w-3" strokeWidth={2} aria-hidden />
                        {i18n._(msg`Rimuovi`)}
                      </button>
                    ) : (
                      <span />
                    )}
                    <button
                      type="button"
                      onClick={applyLink}
                      disabled={!linkUrl.trim()}
                      className={cn(
                        "rounded-md bg-accent px-3 py-1 font-medium text-[11px] text-white",
                        "hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50",
                      )}
                    >
                      {i18n._(msg`Applica`)}
                    </button>
                  </div>
                </PopoverContent>
              </Popover>
              <BubbleButton
                onClick={() => editor?.chain().focus().unsetAllMarks().clearNodes().run()}
                label={i18n._(msg`Rimuovi formattazione`)}
              >
                <RemoveFormatting className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
              </BubbleButton>

              {aiEdit ? (
                <>
                  <span className="ml-auto" aria-hidden />
                  <BubbleButton accent onClick={openAiOnSelection} label={i18n._(msg`Migliora con l'AI`)}>
                    {/* Il glifo di Ando al posto della sparkle: si anima da
                        solo quando il bottone va in hover/focus. */}
                    <LineBlobGlyph size={16} />
                  </BubbleButton>
                </>
              ) : null}
            </div>
          ) : null}
          <EditorContent editor={editor} />
          {footer ? (
            <div className="border-hairline border-t px-3 py-2.5">{footer}</div>
          ) : null}
        </div>

        {aiEdit ? (
          <AIEditBubble
            ref={aiPanelRef}
            open={aiOpen}
            style={{
              position: "absolute",
              left: aiAnchor?.left ?? 0,
              top: aiAnchor?.top ?? "100%",
              maxHeight: aiAnchor?.maxHeight,
            }}
            busy={aiBusy}
            error={aiError}
            changes={aiDiff?.changes}
            original={aiDiff?.original}
            decisions={aiDecisions}
            onDecision={onAiDecision}
            onRun={(instruction) => void runAiEdit(instruction)}
            onApply={applyAiEdit}
            onCancel={closeAi}
            onRetry={() => void runAiEdit(lastInstruction)}
            canRetry={Boolean(lastInstruction)}
          />
        ) : null}
      </div>
    );
  },
);
RichEmailBody.displayName = "RichEmailBody";

// ---------------------------------------------------------------------------
// Test helper — build an editor with the exact email config, no React tree.
// Mirrors ``_createTestEditor`` in RichTextEditor. @internal
// ---------------------------------------------------------------------------

/** @internal — exposed for unit tests. */
export function _createTestEmailEditor(opts?: {
  content?: JSONContent | string;
}): TiptapEditor {
  return new Editor({
    extensions: buildEmailExtensions(),
    content: opts?.content ?? "",
  });
}
